'use client';

import React, { useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useAuthBoot } from '../context/AuthBootContext';
import { useConsentContext } from '../context/ConsentContext';
import { cn } from '@/lib/utils/cn';
import { AnalyticsEventName } from '@/lib/consts/analytics';
import { LoggerEventNames } from '@/lib/consts/logger';
import { sendPreLoginPublisherWebhookEvents } from '@/lib/utils/login';
import { useLoginDisplay } from '../hooks/useLoginDisplay';
import { isWebviewBrowser } from '@/lib/utils/webview';
import { isAutoLoginFlow } from '@/lib/utils/auth';
import { getDeviceOrientation } from '@/lib/utils/deviceOrientation';
import analyticsService from '@/lib/services/analytics';
import coralogixRumService from '@/lib/services/coralogixRum';
import {
  LOGIN_BUTTON_HEIGHT_CLASS,
  LOGIN_BUTTON_MARGIN_CLASS,
  LOGIN_BUTTON_SLOT_PX,
} from './Buttons/loginButtonLayout';

function useLoginButtonSlotCount(): number {
  const { authBoot, isWebview } = useAuthBoot();
  const { showOtpButton } = useLoginDisplay();
  const { fbOn, googleOn, appleOn, usernamePasswordOn, userTokenOn } =
    authBoot.integration.playersAuthentication;

  return [
    showOtpButton,
    !isWebview && fbOn,
    !isWebview && googleOn,
    !isWebview && appleOn,
    !isWebview && usernamePasswordOn,
    !isWebview && userTokenOn,
  ].filter(Boolean).length;
}

/**
 * Invisible spacers matching SocialLoginButton geometry (h-12 + mb-3).
 * No pulse/skeleton — preserves existing design while holding layout for CLS.
 */
function LoginButtonsPlaceholder(): React.ReactElement {
  const placeholderCount = useLoginButtonSlotCount();

  return (
    <React.Fragment>
      {Array.from({ length: placeholderCount }, (_, index) => (
        <div
          key={index}
          className={`${LOGIN_BUTTON_MARGIN_CLASS} ${LOGIN_BUTTON_HEIGHT_CLASS} invisible w-full max-w-[400px]`}
          aria-hidden="true"
          data-testid="login-button-placeholder"
        />
      ))}
    </React.Fragment>
  );
}

const LoginButtonsPanel = dynamic(
  () => import('./LoginButtonsPanel').then((module) => module.LoginButtonsPanel),
  {
    ssr: false,
    loading: LoginButtonsPlaceholder,
  }
);

let loginDisplayedSent = false;

export const LoginButtonsBlock = (): React.ReactElement => {
  const { authBoot } = useAuthBoot();
  const { isLoginDisabledByConsent } = useConsentContext();
  const slotCount = useLoginButtonSlotCount();
  const reservedHeightPx = useMemo(() => slotCount * LOGIN_BUTTON_SLOT_PX, [slotCount]);

  useEffect(() => {
    if (loginDisplayedSent || !authBoot.featureFlags || isAutoLoginFlow()) return;

    const sendLoginLandEvent = async () => {
      loginDisplayedSent = true;
      try {
        await sendPreLoginPublisherWebhookEvents();
        if (!isWebviewBrowser()) {
          analyticsService.trackEvent(AnalyticsEventName.LOGIN_LAND, {
            device_orientation: getDeviceOrientation(),
          });
        }
      } catch (error) {
        coralogixRumService.sendErrorEvent(LoggerEventNames.PUBLISHER_WEBHOOK_ERROR, { error });
      }
    };

    sendLoginLandEvent();
  }, [authBoot.featureFlags]);

  return (
    <div
      className={cn(
        'mt-5 flex w-full max-w-[400px] flex-col items-center',
        isLoginDisabledByConsent ? 'opacity-50' : ''
      )}
      aria-disabled={isLoginDisabledByConsent || undefined}
      data-testid="login-buttons-block"
      style={{ minHeight: reservedHeightPx }}
    >
      <LoginButtonsPanel />
    </div>
  );
};
